/**
 * Controles del formulario "Mis datos". Todos con targets ≥44 px, sin `$` y con `null` (no 0) para "vacío".
 */
import { clsx } from "clsx";
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { Minus, Plus, Search, X } from "lucide-react";
import { norm } from "@/engine/excel/fns";

// ───────────── Chips (radio group) ─────────────
export interface OpcionChip<T extends string | number> { valor: T; etiqueta: string; ayuda?: string }
export function Chips<T extends string | number>({ opciones, valor, onChange, etiqueta, className, tamano = "md" }: {
  opciones: OpcionChip<T>[]; valor: T | null; onChange: (v: T) => void; etiqueta: string; className?: string; tamano?: "sm" | "md";
}) {
  return (
    <div role="radiogroup" aria-label={etiqueta} className={clsx("flex flex-wrap gap-2", className)}>
      {opciones.map((o) => {
        const activo = o.valor === valor;
        return (
          <button key={String(o.valor)} type="button" role="radio" aria-checked={activo} title={o.ayuda}
            onClick={() => onChange(o.valor)}
            className={clsx("rounded-full border px-4 font-medium transition-colors", tamano === "md" ? "min-h-11 text-sm" : "min-h-9 text-xs",
              activo ? "border-primary bg-primary text-white" : "border-borde bg-white text-tinta hover:border-primary")}>
            {o.etiqueta}
          </button>
        );
      })}
    </div>
  );
}

// ───────────── Tiles (tarjetas grandes de opción) ─────────────
export interface OpcionTile<T extends string> { valor: T; titulo: string; linea?: string; icono?: ReactNode; pie?: string }
export function Tiles<T extends string>({ opciones, valor, onChange, etiqueta, columnas = 2 }: {
  opciones: OpcionTile<T>[]; valor: T | null; onChange: (v: T) => void; etiqueta: string; columnas?: 1 | 2 | 3;
}) {
  return (
    <div role="radiogroup" aria-label={etiqueta} className={clsx("grid gap-3", columnas === 1 && "grid-cols-1", columnas === 2 && "grid-cols-1 sm:grid-cols-2", columnas === 3 && "grid-cols-1 sm:grid-cols-2 md:grid-cols-3")}>
      {opciones.map((o) => {
        const activo = o.valor === valor;
        return (
          <button key={o.valor} type="button" role="radio" aria-checked={activo} onClick={() => onChange(o.valor)}
            className={clsx("flex min-h-16 items-start gap-3 rounded-md border bg-white p-4 text-left transition-colors", activo ? "border-primary ring-2 ring-primary/20" : "border-borde hover:border-primary")}>
            {o.icono && <span className="mt-0.5 text-primary" aria-hidden>{o.icono}</span>}
            <span className="flex flex-col gap-0.5">
              <span className="font-semibold text-tinta">{o.titulo}</span>
              {o.linea && <span className="text-sm text-tinta-suave">{o.linea}</span>}
              {o.pie && <span className="text-xs text-gray-400">{o.pie}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ───────────── Números ─────────────
const fmtGrupo = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
/** "1,200,000" | "1200000" | "1,200.50" → número; "" → null. Nunca acepta `$`. */
export function parseDinero(txt: string): number | null {
  const t = txt.replace(/[\s,$]/g, "").replace(/(MXN|USD)$/i, "");
  if (t === "" || t === "-") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

const claseInput = "h-11 w-full rounded-md border border-borde bg-white px-3 text-base text-tinta placeholder:text-gray-400 focus:border-primary";

/** Monto de dinero: separadores al salir del campo, dígitos crudos al editar. Vacío = null. */
export function Dinero({ valor, onChange, moneda = "MXN", id, placeholder, tamano = "md", permitirNegativo = false, ariaLabel, onEnter }: {
  valor: number | null; onChange: (v: number | null) => void; moneda?: string; id?: string; placeholder?: string; tamano?: "md" | "lg";
  permitirNegativo?: boolean; ariaLabel?: string; onEnter?: () => void;
}) {
  const [foco, setFoco] = useState(false);
  const [texto, setTexto] = useState(valor === null ? "" : String(valor));
  useEffect(() => { if (!foco) setTexto(valor === null ? "" : String(valor)); }, [valor, foco]);
  const mostrado = foco ? texto : valor === null ? "" : fmtGrupo.format(valor);
  return (
    <div className="relative">
      <input id={id} type="text" inputMode="decimal" enterKeyHint="next" autoComplete="off" aria-label={ariaLabel} placeholder={placeholder ?? "0"}
        className={clsx(claseInput, "tabular pr-14", tamano === "lg" && "h-14 text-2xl")}
        value={mostrado}
        onFocus={() => { setFoco(true); setTexto(valor === null ? "" : String(valor)); }}
        onBlur={() => setFoco(false)}
        onKeyDown={(e) => { if (e.key === "Enter" && onEnter) { e.preventDefault(); onEnter(); } }}
        onChange={(e) => {
          const raw = e.target.value;
          setTexto(raw);
          let n = parseDinero(raw);
          if (n !== null && !permitirNegativo && n < 0) n = Math.abs(n);
          onChange(n);
        }} />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-tinta-suave">{moneda}</span>
    </div>
  );
}

/** Porcentaje: el usuario escribe 12 → se guarda 0.12. Si escribe 0.12, se interpreta como 0.12 % y se le avisa. */
export function Porcentaje({ valor, onChange, id, ariaLabel, max = 1000 }: { valor: number | null; onChange: (v: number | null) => void; id?: string; ariaLabel?: string; max?: number }) {
  const [foco, setFoco] = useState(false);
  const aTexto = (v: number | null) => (v === null ? "" : String(Math.round(v * 100 * 10000) / 10000));
  const [texto, setTexto] = useState(aTexto(valor));
  useEffect(() => { if (!foco) setTexto(aTexto(valor)); }, [valor, foco]);
  const pareceFraccion = valor !== null && valor > 0 && valor < 0.01;
  return (
    <div>
      <div className="relative">
        <input id={id} type="text" inputMode="decimal" aria-label={ariaLabel} placeholder="0" className={clsx(claseInput, "tabular pr-10")}
          value={texto} onFocus={() => setFoco(true)} onBlur={() => setFoco(false)}
          onChange={(e) => {
            setTexto(e.target.value);
            const t = e.target.value.replace(/[\s%,]/g, "");
            if (t === "") return onChange(null);
            const n = Number(t);
            if (Number.isFinite(n) && Math.abs(n) <= max) onChange(n / 100);
          }} />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-tinta-suave">%</span>
      </div>
      {pareceFraccion && <p className="mt-1 text-xs text-amber-800">Quedó en {texto} %. Si querías decir {Math.round((valor ?? 0) * 10000)} %, escribe ese número.</p>}
    </div>
  );
}

/** Entero con botones − / +. */
export function Stepper({ valor, onChange, min, max, paso = 1, sufijo, id, ariaLabel }: {
  valor: number | null; onChange: (v: number | null) => void; min: number; max: number; paso?: number; sufijo?: string; id?: string; ariaLabel?: string;
}) {
  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  return (
    <div className="flex items-center gap-2">
      <button type="button" aria-label="Menos" onClick={() => onChange(clamp((valor ?? min + paso) - paso))} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-borde bg-white hover:border-primary"><Minus className="h-4 w-4" aria-hidden /></button>
      <div className="relative flex-1">
        <input id={id} aria-label={ariaLabel} type="text" inputMode="numeric" className={clsx(claseInput, "tabular text-center", sufijo && "pr-16")}
          value={valor === null ? "" : String(valor)}
          onChange={(e) => {
            const t = e.target.value.replace(/[^\d-]/g, "");
            if (t === "") return onChange(null);
            const n = Number(t);
            if (Number.isFinite(n)) onChange(n);
          }} />
        {sufijo && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-tinta-suave">{sufijo}</span>}
      </div>
      <button type="button" aria-label="Más" onClick={() => onChange(clamp((valor ?? min - paso) + paso))} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-borde bg-white hover:border-primary"><Plus className="h-4 w-4" aria-hidden /></button>
    </div>
  );
}

/** Escala segmentada 1-5 (o 1-10). */
export function Escala({ valor, onChange, min = 1, max = 5, etiqueta, paso = 1 }: { valor: number | null; onChange: (v: number | null) => void; min?: number; max?: number; etiqueta: string; paso?: number }) {
  const vals: number[] = [];
  for (let v = min; v <= max + 1e-9; v += paso) vals.push(Math.round(v * 10) / 10);
  return (
    <div role="radiogroup" aria-label={etiqueta} className="flex gap-1.5">
      {vals.map((v) => (
        <button key={v} type="button" role="radio" aria-checked={valor === v} onClick={() => onChange(valor === v ? null : v)}
          className={clsx("tabular flex h-11 min-w-11 flex-1 items-center justify-center rounded-md border text-sm font-semibold transition-colors",
            valor === v ? "border-primary bg-primary text-white" : "border-borde bg-white text-tinta hover:border-primary")}>{v}</button>
      ))}
    </div>
  );
}

// ───────────── Combobox con búsqueda en memoria ─────────────
export interface ItemCombo { clave: string; etiqueta: string; secundaria?: string; extra?: ReactNode; buscar?: string }
export function Combobox({ items, onElegir, placeholder = "Buscar…", etiqueta, max = 8, autoFocus }: {
  items: ItemCombo[]; onElegir: (clave: string) => void; placeholder?: string; etiqueta: string; max?: number; autoFocus?: boolean;
}) {
  const [q, setQ] = useState("");
  const [activo, setActivo] = useState(0);
  const id = useId();
  const res = useMemo(() => {
    const t = norm(q);
    if (t.length < 2) return [];
    const palabras = t.split(" ").filter(Boolean);
    return items.filter((i) => { const h = norm(`${i.etiqueta} ${i.secundaria ?? ""} ${i.buscar ?? ""}`); return palabras.every((p) => h.includes(p)); }).slice(0, max);
  }, [q, items, max]);
  useEffect(() => setActivo(0), [q]);
  const elegir = (c: string) => { onElegir(c); setQ(""); };
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-tinta-suave" aria-hidden />
      <input type="search" role="combobox" aria-label={etiqueta} aria-expanded={res.length > 0} aria-controls={`${id}-lista`}
        aria-activedescendant={res[activo] ? `${id}-${activo}` : undefined} autoFocus={autoFocus} autoComplete="off"
        className={clsx(claseInput, "pl-9")} placeholder={placeholder} value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setActivo((a) => Math.min(res.length - 1, a + 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActivo((a) => Math.max(0, a - 1)); }
          else if (e.key === "Enter" && res[activo]) { e.preventDefault(); elegir(res[activo].clave); }
          else if (e.key === "Escape") setQ("");
        }} />
      {res.length > 0 && (
        <ul id={`${id}-lista`} role="listbox" className="absolute z-30 mt-1 max-h-80 w-full overflow-y-auto rounded-md border border-borde bg-white shadow-lg">
          {res.map((r, i) => (
            <li key={r.clave} id={`${id}-${i}`} role="option" aria-selected={i === activo}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => elegir(r.clave)}
                className={clsx("flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left", i === activo ? "bg-gray-100" : "hover:bg-gray-50")}>
                <span className="flex flex-col"><span className="font-medium">{r.etiqueta}</span>{r.secundaria && <span className="text-xs text-tinta-suave">{r.secundaria}</span>}</span>
                {r.extra}
              </button>
            </li>
          ))}
        </ul>
      )}
      {norm(q).length >= 2 && res.length === 0 && <p className="mt-2 text-sm text-tinta-suave">Sin resultados. Elige el tipo más parecido abajo o "12 · Otros".</p>}
    </div>
  );
}

// ───────────── Hoja inferior (móvil) / diálogo (desktop) ─────────────
export function HojaInferior({ abierta, onCerrar, titulo, children, pie }: { abierta: boolean; onCerrar: () => void; titulo: string; children: ReactNode; pie?: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (abierta && !d.open) d.showModal();
    if (!abierta && d.open) d.close();
  }, [abierta]);
  return (
    <dialog ref={ref} onClose={onCerrar} onClick={(e) => { if (e.target === ref.current) onCerrar(); }}
      className="hoja-inferior m-0 mt-auto w-full max-w-none rounded-t-2xl border border-borde bg-fondo p-0 text-tinta shadow-2xl backdrop:bg-black/40 md:m-auto md:max-w-2xl md:rounded-2xl">
      <div className="flex max-h-[88vh] flex-col">
        <div className="flex items-center justify-between border-b border-borde bg-white px-5 py-3 md:rounded-t-2xl">
          <h2 className="text-lg font-semibold">{titulo}</h2>
          <button type="button" onClick={onCerrar} aria-label="Cerrar" className="rounded p-2 hover:bg-gray-100"><X className="h-5 w-5" aria-hidden /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
        {pie && <div className="border-t border-borde bg-white px-5 py-3" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>{pie}</div>}
      </div>
    </dialog>
  );
}

// ───────────── Barra de progreso ─────────────
export function Barra({ valor, tono = "primario", etiqueta }: { valor: number; tono?: "primario" | "ok" | "neutro"; etiqueta?: string }) {
  const pct = Math.max(0, Math.min(1, valor));
  return (
    <div className="h-2 w-full rounded-full bg-[#e3e9f1]" role="progressbar" aria-label={etiqueta} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct * 100)}>
      <div className={clsx("h-2 rounded-full transition-[width] duration-300 motion-reduce:transition-none", tono === "primario" && "bg-primary", tono === "ok" && "bg-emerald-700", tono === "neutro" && "bg-gray-400")} style={{ width: `${pct * 100}%` }} />
    </div>
  );
}
